import { apiClient } from "./httpClient";

export type ApiEnvelope<T> = {
  success: boolean;
  message?: string;
  data?: T;
  errors?: unknown;
};

export type LoginResponse = {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  userId: string;
  email: string;
  organizationId?: number | null;
};

type RefreshResponse = LoginResponse;

export type CurrentUserProfile = {
  userId: string;
  username: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  branchId?: number | null;
  branchName?: string | null;
};

export type AdminUserRow = {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  isLocked: boolean;
};

const AUTH_LOGIN_URL = "/auth/login";
const AUTH_LOGIN_FALLBACK_URL = "/auth/loginPayload";
const AUTH_REFRESH_URL = "/auth/refresh";
const AUTH_ME_URL = "/auth/me";

function getDeviceId() {
  const key = "transport-device-id";
  const existing = typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  if (existing) return existing;

  const created =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `web-${Date.now()}`;

  if (typeof localStorage !== "undefined") {
    localStorage.setItem(key, created);
  }

  return created;
}

function buildLoginPayload(email: string, password: string) {
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "unknown-agent";
  const platform = typeof navigator !== "undefined" ? navigator.platform : "unknown-platform";
  const deviceId = getDeviceId();

  return {
    email,
    password,
    deviceId,
    deviceName: `${platform} / ${userAgent}`,
    ipAddress: "localhost"
  };
}

export async function loginApi(email: string, password: string) {
  const payload = buildLoginPayload(email, password);
  let response;
  try {
    response = await apiClient.post<ApiEnvelope<LoginResponse>>(AUTH_LOGIN_URL, payload);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      response = await apiClient.post<ApiEnvelope<LoginResponse>>(AUTH_LOGIN_FALLBACK_URL, payload);
    } else {
      throw error;
    }
  }

  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? "Login failed");
  }

  return response.data.data;
}

export async function refreshTokenApi(refreshToken: string) {
  const response = await apiClient.post<ApiEnvelope<RefreshResponse>>(
    AUTH_REFRESH_URL,
    { refreshToken },
    {
      headers: {
        "X-Device-Id": getDeviceId()
      }
    }
  );

  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? "Session refresh failed");
  }

  return response.data.data;
}

export async function getCurrentUserProfileApi() {
  const response = await apiClient.get<ApiEnvelope<CurrentUserProfile>>(AUTH_ME_URL);
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.message ?? "Failed to load current user profile.");
  }
  return response.data.data;
}

export async function listAdminUsers(search: string, page: number, pageSize: number) {
  const response = await apiClient.get<ApiEnvelope<AdminUserRow[]> | AdminUserRow[]>("/admin/users", {
    params: {
      q: search || undefined,
      page,
      pageSize
    }
  });
  const payload = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);

  return {
    data: payload,
    total: Number(response.headers["x-total-count"] ?? payload.length)
  };
}

export async function listAdminRoles() {
  const response = await apiClient.get<ApiEnvelope<string[]> | string[]>("/admin/roles");
  const payload = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
  return Array.isArray(payload) ? payload : [];
}

export async function createAdminUser(payload: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
}) {
  return apiClient.post("/admin/users", payload);
}

export async function updateAdminUser(
  id: string,
  payload: {
    userId?: string;
    email: string;
    firstName?: string;
    lastName?: string;
    roles: string[];
  }
) {
  return apiClient.put(`/admin/users/${id}`, payload);
}

export async function deleteAdminUser(id: string) {
  return apiClient.delete(`/admin/users/${id}`);
}

export async function lockAdminUser(id: string) {
  return apiClient.post(`/admin/users/${id}/lock`);
}

export async function unlockAdminUser(id: string) {
  return apiClient.post(`/admin/users/${id}/unlock`);
}

export async function resetAdminUserPassword(id: string, newPassword: string) {
  return apiClient.post(`/admin/users/${id}/reset-password`, { newPassword });
}
